import React from 'react'
import './MessageCenterSkeletonStyle.css'
import { Col } from 'react-bootstrap'
import { SkeletonComponent } from '@syncfusion/ej2-react-notifications'
import { TableSkeleton, type TableSkeletonColumn } from '@components/ui'

type MessageCenterSkeletonVariant = 'list' | 'contact' | 'calls'

type MessageCenterSkeletonProps = {
  hideHeader?: boolean
  variant?: MessageCenterSkeletonVariant
}

const CONTACT_COLUMNS: TableSkeletonColumn[] = [
  { width: '18%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '18%', kind: 'text' },
  { width: '20%', kind: 'text' },
  { width: '8%', kind: 'text' },
  { width: '12%', kind: 'actions' },
]

const CALLS_COLUMNS: TableSkeletonColumn[] = [
  { width: '8%', kind: 'text' },
  { width: '14%', kind: 'text' },
  { width: '14%', kind: 'text' },
  { width: '14%', kind: 'text' },
  { width: '12%', kind: 'badge' },
  { width: '12%', kind: 'badge' },
  { width: '10%', kind: 'text' },
  { width: '14%', kind: 'actions' },
]

const CALLS_METRIC_COUNT = 8

function MessageCenterListSkeleton({
  hideHeader,
}: {
  hideHeader: boolean
}): React.ReactElement {
  return (
    <div className={`mc-skeleton${hideHeader ? ' mc-skeleton--tab' : ''}`}>
      {!hideHeader && (
        <div className="mcs-tabs-container">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="mcs-tab">
              <SkeletonComponent
                shape="Circle"
                width="13px"
                shimmerEffect="Fade"
              />
              <SkeletonComponent
                shape="Rectangle"
                width="20px"
                height="10px"
                shimmerEffect="Fade"
              />
            </div>
          ))}
        </div>
      )}
      <Col md="auto" className="mcs-panel">
        <div className="mcs-filter">
          <SkeletonComponent shape="Text" width="104px" height="14px" />
          <div className="mcs-row-2">
            <SkeletonComponent
              shape="Circle"
              width="13px"
              shimmerEffect="Fade"
            />
            <SkeletonComponent
              shape="Text"
              width="20px"
              height="11px"
              shimmerEffect="Fade"
            />
          </div>
          <SkeletonComponent shape="Text" width="34px" height="12.25px" />
          <div className="mcs-row-4">
            <SkeletonComponent
              shape="Circle"
              width="12px"
              shimmerEffect="Fade"
            />
            <div className="d-flex align-center mcs-dd">
              <SkeletonComponent
                shape="Rectangle"
                width="16px"
                height="8px"
                shimmerEffect="Fade"
              />
            </div>
          </div>
        </div>
        <SkeletonComponent
          shape="Rectangle"
          width="320px"
          cssClass="mcs-list"
        ></SkeletonComponent>
      </Col>
    </div>
  )
}

function MessageCenterSkeleton({
  hideHeader = false,
  variant = 'list',
}: MessageCenterSkeletonProps): React.ReactElement {
  if (variant === 'list') {
    return <MessageCenterListSkeleton hideHeader={hideHeader} />
  }

  return (
    <div
      className={`mc-skeleton mc-skeleton--table${
        variant === 'calls' ? ' mc-skeleton--calls' : ''
      }`}
      aria-hidden="true"
    >
      {variant === 'calls' ? (
        <div className="mcs-metrics">
          {Array.from({ length: CALLS_METRIC_COUNT }).map((_, index) => (
            <div key={`metric-${index}`} className="mcs-metric">
              <span className="mcs-metric-title" />
              <span className="mcs-metric-value" />
              <span className="mcs-metric-date" />
            </div>
          ))}
        </div>
      ) : null}
      <TableSkeleton
        columns={variant === 'calls' ? CALLS_COLUMNS : CONTACT_COLUMNS}
        actionCount={4}
        rows={8}
        showPagination
      />
    </div>
  )
}

export default MessageCenterSkeleton
